<script>
// トークンの有効期限切れ、キャッシュ強制クリアにより未認証状態になった場合はTOP画面へ戻る。
// サインアウト: 読み出し元にTOP画面への遷移を実装
// 認証チェックの結果未認証発覚: 読み出し元にTOP画面への遷移を実装
// パスワード更新時にトークン取得失敗: 読み出し元にTOP画面への遷移を実装
// S3アクセスの際にトークン取得または、トークンリフレッシュ失敗: トークン取得またはリフレッシュ失敗時にTOP画面への遷移を実装(Mixin)
// API-GWアクセスの際にトークン取得または、トークンリフレッシュ失敗
import { config, CognitoIdentityCredentials } from 'aws-sdk' // 【後で削除】クレデンシャルの更新に必要(S3の更新に必要): トークン取得またはリフレッシュ失敗時にTOP画面への遷移を実装(Mixin)

/**
 * @module components/miiafw/mixin/MiiaFwCognitoMixin
 * @description 認証や、属性情報を取得する機能を集合させたMixin。
 */
export default {
  name: 'MiiaFwCognitoMixin',
  methods: {
    /**
     * Vuexにストアされている'miiaFwStore/updateAuthInfo'を更新する。
     * ちなみにmiiaFwStore/updateAuthInfoは現在使用されていない。
     * @param {Boolean} isAutAuthInfo 'miiaFwStore/updateAuthInfo'の値を変更する。
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_updateAuthInfo (isAuthorized) {
      this.$store.commit('miiaFwStore/updateAuthInfo', isAuthorized)
    },
    /**
     * Vuexにストアされている'miiaFwStore/updateMenu'を更新する。
     * 表示するメニュー項目の設定を行うときに実行する。
     * @param {Array} menu Vuerouterのroutesの要素を並べた配列。
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_updateMenu (menu) {
      this.$store.commit('miiaFwStore/updateMenu', menu)
    },
    /**
     * Vuexにストアされている'miiaFwStore/updateAttributes'を更新する。
     * 属性情報を更新するときに使用する。
     * @param {Object} attributes 認可情報
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_updateAttributes (attributes) {
      this.$store.commit('miiaFwStore/updateAttributes', attributes)
    },
    /**
     * Vuexにストアされている'miiaFwStore/updateSensitiveInfo'を更新する。
     * センシティブ情報を設定するときに使用する
     * @param {Object} obj センシティブ情報
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_updateSensitiveInfo (obj) {
      this.$store.commit('miiaFwStore/updateSensitiveInfo', obj)
    },
    /**
     * Vuexにストアされている'miiaFwStore/updateIsAuthorized'を更新する。
     * 認証が新しく実行されて、成功した際にtrueにする。
     * @param {Boolean} isAuthorized 認証が更新に成功したかどうか。
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_updateIsAuthorized (isAuthorized) {
      this.$store.commit('miiaFwStore/updateIsAuthorized', isAuthorized)
    },
    /**
     * Vuexにストアされている属性情報('miiaFwStore/updateAttributes')、
     * センシティブ情報('miiaFwStore/updateSensitiveInfo')、
     * 認証情報('miiaFwStore/updateIsAuthorized')をクリーンアップする。
     * @param {} - 引数無し
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_cleanupAuthInfoInStore () {
      this.$store.commit('miiaFwStore/updateAttributes', undefined) // cognitoAuth.attributesの初期値はundefined
      this.$store.commit('miiaFwStore/updateSensitiveInfo', undefined) // cognitoAuth.sensitiveInfoの初期値はundefined
      this.$store.commit('miiaFwStore/updateIsAuthorized', false)
    },
    /**
     * Vuexにストアされている'miiaFwStore/updateRefreshState'を書き換える。
     * トークンのリフレッシュ処理が必要な時にtrueに切り変わる。
     * @param {Boolean|Object} onRefresh リフレッシュ処理が必要かどうか。onRefreshはBooreanか、BooleanのonRefreshを要素としてもつオブジェクトである。
     * @param {Boolean} onRefresh.onReflesh リフレッシュ処理が必要かどうか。
     * @param {String} [errorMessage = undefined] リフレッシュ処理の際にエラーが発生した時に格納するエラーメッセージ。
     * @return {} 戻り値なし
     */
    miiafwCognitolibs_updateRefreshState (onRefresh, errorMessage) {
      if (errorMessage === undefined) {
        this.$store.commit('miiaFwStore/updateRefreshState', { onRefresh: onRefresh })
      } else {
        this.$store.commit('miiaFwStore/updateRefreshState', { onRefresh: onRefresh, error: errorMessage })
      }
    },
    /**
     * Cognitoのサインアップ処理を行う。
     * @param {String} userName ユーザーID
     * @param {String} email メールアドレス
     * @param {String} password パスワード
     * @return {Promise<String|Object>} If resolved then 'サインアップに成功しました。'.
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_signup (userName, email, password) {
      const args = {
        function: 'sign_up',
        params: {
          user_id: userName,
          email: email,
          password: password,
          system_id: this.$sysId
        }
      }
      return new Promise((resolve, reject) => {
        this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', {}, args, 'json').then((data) => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            resolve(data.result)
          }
        }).catch((error) => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
      })
    },
    /**
     * Cognitoのサインアップしたユーザーを認証コードを用いてアクティベートする。
     * @param {String} userName ユーザー
     * @param {String} verificationCode 認証コード
     * @return {Promise<String|Object>} If resolved then 'アクティベートに成功しました。'.
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_activate (userName, verificationCode) {
      const args = {
        function: 'activate',
        params: {
          user_id: userName,
          verification_code: verificationCode
        }
      }
      return new Promise((resolve, reject) => {
        this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', {}, args, 'json').then((data) => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            resolve(data.result)
          }
        }).catch((error) => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
      })
    },
    /**
     * Cognitoのサインイン処理を行う。
     * サインインに成功した場合はセッションストレージの認証情報を更新してから、認証情報を返す。
     * @param {String} userName ユーザーID
     * @param {String} password パスワード
     * @return {Promise<Object>} If resolved then
     * {'isExpired': (Boolean)有効期限切れかどうか,
     * 'isUpdated': (Boolean->true) 認証がアップデートされたかどうか,
     * 'userId': (String)ユーザーID,
     * 'tokens': {'provider': (String->'cognito')認証を提供するプロバイダ,
     * 'access_token': (String)アクセストークン,
     * 'refresh_token': (String)リフレッシュトークン,
     * 'id_token': (String)IDトークン }}.
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_signin (userName, password) {
      const args = {
        function: 'sign_in',
        params: {
          user_id: userName,
          password: password,
          system_id: this.$sysId
        }
      }
      if (this.$authorization.expirationTime < Number.MAX_VALUE) {
        args.params.expiration_date_n = this.$authorization.expirationTime
      }
      return new Promise((resolve, reject) => {
        this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', {}, args, 'json').then((data) => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            this.miiafwCognitolibs_setTokens(data.result.tokens)
            this.miiafwCognitolibs_updateIsAuthorized(true)
            this.miiafwCognitolibs_setUserIdToStorage(userName)
            this.miiafwCognitolibs_setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
            this.miiafwCognitolibs_updateCredentials(data.result.tokens) // 【後で削除】S3でAWS SDKを使用するために必要
            resolve(data.result)
          }
        }).catch((error) => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          // miiafwCognitolibs_procAtSignout()は失敗しない。
          this.miiafwCognitolibs_procAtSignout().then((result) => {
            reject({ message: error.message })
          })
        })
      })
    },
    /**
     * サインアウト処理を行う（任意の認証プロバイダで動作）。
     * @param {} - 引数なし
     * @return {Promise<String|Object>} サインアウト処理に成功した際のメッセージをStringで返す。サインアウト処理でエラーが起きた場合はメッセージをオブジェクト{'message': '[エラーメッセージ]'}で返す。必ずresolvedになる。
     */
    miiafwCognitolibs_signout () {
      return new Promise((resolve, reject) => {
        // miiafwCognitolibs_procAtSignout()は失敗しない。
        this.miiafwCognitolibs_procAtSignout().then((result) => {
          resolve(result)
        })
      })
    },
    /**
     * Cognitoのユーザーのパスワードをリセットする。
     * @param {String} userName ユーザーID
     * @return {Promise<String|Object>} If resolved then 'パスワードリセット成功しました。'.
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_passwardReset (userName) {
      const args = {
        function: 'reset_passward',
        params: {
          user_id: userName
        }
      }
      return new Promise((resolve, reject) => {
        this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', {}, args, 'json').then((data) => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            resolve(data.result)
          }
        }).catch((error) => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
      })
    },
    /**
     * Cognitoのユーザーのパスワードを初期化する（認証コードによる認証が必要）。
     * @param {String} userName ユーザーID
     * @param {String} verificationCode 認証コード
     * @param {String} password パスワード初期化に用いるパスワード
     * @return {Promise<String|Object>} If resolved then 'パスワード初期化に成功しました。'.
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_passwardInitialize (userName, verificationCode, password) {
      const args = {
        function: 'initialize_passward',
        params: {
          verification_code: verificationCode,
          user_id: userName,
          password: password
        }
      }
      return new Promise((resolve, reject) => {
        this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', {}, args, 'json').then((data) => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            resolve(data.result)
          }
        }).catch((error) => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
      })
    },
    /**
     * Cognitoのユーザーのパスワードを変更する。
     * @param {String} currentPassword 現在のパスワード
     * @param {String} newPassword 新しいパスワード
     * @return {Promise<String|Object>} If resolved then 'パスワード更新に成功しました。'
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_passwardUpdate (currentPassword, newPassword) {
      const tokens = this.miiafwCognitolibs_getTokens()
      return new Promise((resolve, reject) => {
        if (tokens === null) {
          reject({ message: '未認証のユーザのパスワード更新が要求されました。' })
          this.miiafwCognitolibs_procAtSignout().then((result) => {
            this.miiafwlibs_alert('未認証のユーザのパスワード更新が要求されました。', () => {
              if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
            })
          })
        } else {
          const args = {
            function: 'update_passward',
            params: {
              current_password: currentPassword,
              new_password: newPassword
            }
          }
          this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', this.$authorization.method.toLocaleLowerCase() === 'custom_authorizer' ? { 'X-miiafw-token': tokens.access_token } : {}, args, 'json').then((data) => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              reject({ message: data.error })
            } else {
              resolve(data.result)
            }
          }).catch((error) => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            reject({ message: error.message })
          })
        }
      })
    },
    /**
     * miiafwCognitolibs_signoutなどが呼び出す、サインアウト処理を行う本体部分。
     * セッション情報をクリアする。
     * @param {} - 引数なし
     * @return {Promise<String|Object>} サインアウト処理に成功した際のメッセージをStringで返す。サインアウト処理でAPIの呼び出しにエラーが起きた場合はメッセージをオブジェクト{'message': '[エラーメッセージ]'}で返す。必ずresolvedになる。
     */
    miiafwCognitolibs_procAtSignout () {
      const tokens = this.miiafwCognitolibs_getTokens()
      this.miiafwCognitolibs_clearTokens()
      this.miiafwCognitolibs_cleanupAuthInfoInStore()
      // 【後で削除】S3でAWS SDKを使用するために必要
      // サインアウト実行後に必ずキャッシュクリア(ユーザを変えてサインインしたときにエラーとなる。)
      if (config.credentials != null && config.credentials.clearCachedId != null) {
        config.credentials.clearCachedId()
      }
      return new Promise((resolve, reject) => {
        if (tokens === null) {
          resolve('サインアウト不要です。')
        } else {
          if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'cognito') {
            const args = {
              function: 'sign_out',
              params: {}
            }
            this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', this.$authorization.method.toLocaleLowerCase() === 'custom_authorizer' ? { 'X-miiafw-token': tokens.access_token } : {}, args, 'json').then((data) => {
              if (typeof data === 'string') {
                // IE対応
                data = JSON.parse(data)
              }
              if (Object.prototype.hasOwnProperty.call(data, 'error')) {
                // ハンドリングされたエラー処理
                resolve(data.error)
              } else {
                resolve('サインアウトが成功しました。')
              }
            }).catch((error) => {
              if (typeof error === 'string') {
                // IE対応
                error = JSON.parse(error)
              }
              resolve({ message: error.message })
            })
          } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'google') {
            const authInstance = gapi.auth2.getAuthInstance() // eslint-disable-line
            authInstance.then(() => { // onInit
              const isSignedIn = authInstance.isSignedIn.get()
              if (isSignedIn) {
                authInstance.disconnect().then(() => {
                  resolve('サインアウトが成功しました。')
                }).catch((error) => {
                  resolve({ message: error })
                })
              } else {
                resolve('既にサインアウトされています。')
              }
            }, (error) => { // onError
              resolve(error)
            })
          } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'facebook') {
            // FB.getLoginStatus()は、アクセス先がHTTPSでないとログイン状態を取得できない。
            window.FB.getLoginStatus((response) => {
              // response.authResponse.accessTokenがあれば成功とみなせる
              if (response && response.authResponse && response.authResponse.accessToken) {
                window.FB.logout((response) => {
                  resolve('サインアウトが成功しました。')
                })
              } else {
                resolve('既にサインアウトされています。')
              }
            })
          } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'twitter') {
            // 未実装
          } else {
            reject({ message: '対応していないIDプロバイダが設定されました。' })
          }
        }
      })
    },
    /**
     * 認証されているかどうかをチェックする。
     * 認証されていればIDプロバイダを返し、認証されていなかったりエラーが起こったりしたときにはサインアウト処理を行いエラーメッセージを返す。
     * @param {} - 引数なし
     * @return {Promise<Object>} If resolved then {provider: (str)IDプロバイダ, isRefreshed: (boolean)トークンをリフレッシュしたか}.
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_isAuthenticated () {
      const tokens = this.miiafwCognitolibs_getTokens()
      return new Promise((resolve, reject) => {
        if (tokens === null) {
          // miiafwCognitolibs_procAtSignout()は失敗しない。
          this.miiafwCognitolibs_procAtSignout().then((result) => {
            reject({ message: 'サインアウト中です。' })
          })
        } else {
          if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'cognito') {
            const args = { function: 'token_check', params: {} }
            this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', this.$authorization.method.toLocaleLowerCase() === 'custom_authorizer' ? { 'X-miiafw-token': tokens.access_token } : {}, args, 'json').then((data) => {
              if (typeof data === 'string') {
                // IE対応
                data = JSON.parse(data)
              }
              if (Object.prototype.hasOwnProperty.call(data, 'error')) {
                // ハンドリングされたエラー処理
                // miiafwCognitolibs_procAtSignout()は失敗しない。
                this.miiafwCognitolibs_procAtSignout().then((result) => {
                  reject({ message: data.error })
                })
              } else {
                if (data.result.isValid && data.result.userId) {
                  // this.miiafwCognitolibs_setTokens(tokens) // トークンはリフレッシュ後でもSession StrageやCookieに残るので不要
                  // this.miiafwCognitolibs_setUserIdToStorage(data.result.userId) // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
                  this.miiafwCognitolibs_updateCredentials(tokens) // リフレッシュ後に揮発するので要設定後で削除】S3でAWS SDKを使用するために必要
                  this.miiafwCognitolibs_updateIsAuthorized(true) // リフレッシュ後に揮発するので要設定
                  resolve({ provider: tokens.provider, isRefreshed: false })
                } else {
                  this.miiafwCognitolibs_refreshToken().then((argTokens) => {
                    resolve({ provider: tokens.provider, isRefreshed: true })
                  }).catch((refreshError) => {
                    // miiafwCognitolibs_procAtSignout()は失敗しない。
                    this.miiafwCognitolibs_procAtSignout().then((result) => {
                      reject({ message: refreshError.message })
                    })
                  })
                }
              }
            }).catch((error) => {
              if (typeof error === 'string') {
                // IE対応
                error = JSON.parse(error)
              }
              // miiafwCognitolibs_procAtSignout()は失敗しない。
              this.miiafwCognitolibs_procAtSignout().then((result) => {
                reject({ message: error.message })
              })
            })
          } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'google') {
            const signinStatusCheck = () => {
              this.miiafwCognitolibs_googleCheckStatus(
                () => { resolve({ provider: tokens.provider, isRefreshed: false }) },
                (msg) => { reject({ message: msg }) },
                () => {
                  this.miiafwCognitolibs_procAtSignout().then((result) => {
                    reject({ message: '認証画面が閉じられたため、サインアウトしました。' })
                  })
                }
              )
            }
            const timer = setInterval(() => {
              if (gapi && gapi.auth2) { // eslint-disable-line
                clearInterval(timer)
                signinStatusCheck()
              }
            }, 100)
          } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'facebook') {
            this.miiafwCognitolibs_fbCheckStatus(
              () => { resolve({ provider: tokens.provider, isRefreshed: false }) },
              (msg) => { reject({ message: msg }) },
              () => {
                this.miiafwCognitolibs_procAtSignout().then((result) => {
                  reject({ message: '認証画面が閉じられたため、サインアウトしました。' })
                })
              }
            )
          } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'twitter') {
            // console.log('twitter')
            resolve({ provider: tokens.provider, isRefreshed: false })
          } else {
            reject({ message: '対応していないIDプロバイダが設定されました。' })
          }
        }
      })
    },
    /**
     * トークンをリフレッシュする。
     * @param {} - 引数なし
     * @return {Promise<Object>} If resolved then {'access_token': (String)アクセストークン, 'refresh_token': (String)リフレッシュトークン, 'id_token': (String)IDトークン, 'auth_token': Authトークン, 'token_secret': (String)トークンシークレット, 'provider': (String)IDプロバイダ}. セッション情報を返す。
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_refreshToken () {
      // console.log('リフレッシュ要求実行')
      const tokens = this.miiafwCognitolibs_getTokens()
      return new Promise((resolve, reject) => {
        if (tokens === null) {
          reject({ message: 'サインアウト中です。' })
        } else {
          // refreshState.onRefreshがtrue -> falseに変わる(リフレッシュ処理が完了する)のを待つ
          const refreshState = this.$store.getters['miiaFwStore/refreshState']
          if (refreshState && refreshState.onRefresh) {
            const unwatch = this.$store.watch(
              (state, getters) => {
                return state.miiaFwStore.cognitoAuth.refreshState
              },
              (newVal, oldVal) => {
                if (newVal.onRefresh === false) { // リフレッシュが完了した場合
                  if (newVal.error) {
                    reject({ message: newVal.error })
                  } else {
                    // console.log('リフレッシュ処理回避')
                    const refreshedTokens = this.miiafwCognitolibs_getTokens()
                    resolve(refreshedTokens)
                  }
                }
                unwatch() // 監視を解除
              },
              { deep: true }
            )
            // タイムアウト処理はあえて実装しない。(Tokenリフレッシュされずに放置が多重で発生するリスクが極めて低いため)
          } else {
            this.miiafwCognitolibs_updateRefreshState(true) // リフレッシュロックフラグを有効化(miiafwCognitolibs_refreshTokenProc()の処理内で無効化)
            if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'cognito') {
              const now = Date.now()
              // console.log('現在の日時(エポックミリ秒): ' + now)
              // console.log('前回APIコール日時: ' + this.miiafwCognitolibs_getLastAccessDate())
              // console.log('前回APIコール日時からの経過時間[ms]: ' + (now - this.miiafwCognitolibs_getLastAccessDate()))
              // console.log('TTL: ' + this.$authorization.ttl)
              if (now - this.miiafwCognitolibs_getLastAccessDate() < this.$authorization.ttl) {
                // console.log('リフレッシュ処理開始')
                this.miiafwCognitolibs_refreshTokenProc(tokens).then((result) => {
                  resolve(result)
                }).catch((error) => {
                  reject({ message: error.message })
                })
              } else {
                // console.log('パスワードダイアログ表示')
                this.miiafwCognitolibs_recertification(tokens).then((result) => {
                  resolve(result)
                }).catch((error) => {
                  reject({ message: error.message })
                })
              }
            } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
              (tokens.provider.toLowerCase() === 'google' || tokens.provider.toLowerCase() === 'facebook' || tokens.provider.toLowerCase() === 'twitter')) {
              this.miiafwCognitolibs_recertification(tokens).then((result) => {
                resolve(result)
              }).catch((error) => {
                reject({ message: error.message })
              })
            } else {
              this.miiafwCognitolibs_updateRefreshState(false, '対応していないIDプロバイダが設定されました。')
              reject({ message: '対応していないIDプロバイダが設定されました。' })
            }
          }
        }
      })
    },
    /**
     * 再認証およびトークンをリフレッシュする。
     * @param {Object} tokens miiafwCognitolibs_getTokensで取得できるセッション情報
     * @return {Promise<Object>} If resolved then {'access_token': (String)アクセストークン, 'refresh_token': (String)リフレッシュトークン, 'id_token': (String)IDトークン, 'auth_token': Authトークン, 'token_secret': (String)トークンシークレット, 'provider': (String)IDプロバイダ}. セッション情報を返す。
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_recertification (tokens) {
      return new Promise((resolve, reject) => {
        if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'cognito') {
          this.miiafwlibs_pronptDialog(
            'パスワードを入力してください。',
            'password',
            (password) => {
              const userId = this.miiafwCognitolibs_getUserIdToStorage()
              this.miiafwCognitolibs_signin(userId, password).then((result) => {
                // 有効期限切れ対応はしない。(サインイン時のみ)
                this.miiafwCognitolibs_refreshTokenProc(tokens).then((result) => {
                  resolve(result)
                }).catch((error) => {
                  reject({ message: error.message })
                })
              }).catch((error) => {
                this.miiafwlibs_alert(error.message, () => {
                  // 認証成功するまで再帰的に自分を実行
                  this.miiafwCognitolibs_recertification(tokens).then((result) => {
                    resolve(result)
                  }).catch((error) => {
                    reject({ message: error.message })
                  })
                })
              })
            },
            () => {
              this.miiafwlibs_confirm(
                'サインアウトします。よろしいですか？',
                () => {
                  this.miiafwCognitolibs_procAtSignout().then((result) => {
                    this.miiafwCognitolibs_updateRefreshState(false, 'サインアウトされました。(' + Date.now() + ')') // リフレッシュ時の待ち状態解除のため
                    this.miiafwlibs_setLoadingView(false) // ここで処理中表示を解除しないとタイマ発火(最大5秒)まで処理中になってしまう。
                    if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
                  })
                },
                () => {
                  // 再帰的に自分を実行
                  this.miiafwCognitolibs_recertification(tokens).then((result) => {
                    resolve(result)
                  }).catch((error) => {
                    reject({ message: error.message })
                  })
                }
              )
            },
            'large'
          )
        } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'google') {
          this.miiafwCognitolibs_googleCheckStatus(
            () => { resolve('Google認証成功') },
            (msg) => { reject({ message: msg }) },
            () => {
              this.miiafwlibs_confirm(
                'サインアウトします。よろしいですか？',
                () => {
                  this.miiafwCognitolibs_procAtSignout().then((result) => {
                    this.miiafwCognitolibs_updateRefreshState(false, 'サインアウトされました。(' + Date.now() + ')') // リフレッシュ時の待ち状態解除のため
                    this.miiafwlibs_setLoadingView(false) // ここで処理中表示を解除しないとタイマ発火(最大5秒)まで処理中になってしまう。
                    if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
                  })
                },
                () => {
                  // 再帰的に自分を実行
                  this.miiafwCognitolibs_recertification(tokens).then((result) => {
                    resolve(result)
                  }).catch((error) => {
                    reject({ message: error.message })
                  })
                }
              )
            }
          )
        } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'facebook') {
          this.miiafwCognitolibs_fbCheckStatus(
            () => { resolve('Facebook認証成功') },
            (msg) => { reject({ message: msg }) },
            () => {
              this.miiafwlibs_confirm(
                'サインアウトします。よろしいですか？',
                () => {
                  this.miiafwCognitolibs_procAtSignout().then((result) => {
                    this.miiafwCognitolibs_updateRefreshState(false, 'サインアウトされました。(' + Date.now() + ')') // リフレッシュ時の待ち状態解除のため
                    this.miiafwlibs_setLoadingView(false) // ここで処理中表示を解除しないとタイマ発火(最大5秒)まで処理中になってしまう。
                    if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
                  })
                },
                () => {
                  // 再帰的に自分を実行
                  this.miiafwCognitolibs_recertification(tokens).then((result) => {
                    resolve(result)
                  }).catch((error) => {
                    reject({ message: error.message })
                  })
                }
              )
            }
          )
        } else if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'twitter') {
          // TODO: Twitterサインイン画面によるサインイン処理
        } else {
          this.miiafwCognitolibs_updateRefreshState(false, '対応していないIDプロバイダが設定されました。')
          reject({ message: '対応していないIDプロバイダが設定されました。' })
        }
      })
    },
    /**
     * トークンのでのリフレッシュ処理の本体(cognito認証専用)。
     * @param {Object} tokens {refresh_token: '(String)リフレッシュトークン'}
     * @return {Promise<Object>} If resolved then {'access_token': (String)アクセストークン, 'refresh_token': (String)リフレッシュトークン, 'id_token': (String)IDトークン, 'auth_token': Authトークン, 'token_secret': (String)トークンシークレット, 'provider': (String)IDプロバイダ}. セッション情報を返す。
     * If rejected then {'message': '[エラーメッセージ]'}.
     */
    miiafwCognitolibs_refreshTokenProc (tokens) {
      return new Promise((resolve, reject) => {
        const args = {
          function: 'refresh_token',
          params: {
            refresh_token: tokens.refresh_token
          }
        }
        this.miiafwlibs_webApiCall(this.$authEndpoint + '/integrated-auth', 'POST', {}, args, 'json').then((data) => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            this.miiafwCognitolibs_updateRefreshState(false, data.error)
            reject({ message: data.error })
          } else {
            if (data.result.isUpdated) {
              this.miiafwCognitolibs_setTokens(data.result.tokens)
              this.miiafwCognitolibs_setUserIdToStorage(data.result.userId)
              this.miiafwCognitolibs_updateIsAuthorized(true)
              this.miiafwCognitolibs_updateCredentials(data.result.tokens) // 【後で削除】S3でAWS SDKを使用するために必要
            }
            this.miiafwCognitolibs_updateRefreshState(false)
            resolve(data.result.tokens)
          }
        }).catch((error) => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          this.miiafwCognitolibs_updateRefreshState(false, error.message)
          reject({ message: error.message })
        })
      })
    },
    /**
     * API-GWが提供する関数を実行する(POSTメソッド限定)。
     * @param {String} url WebAPIのURL
     * @param {String} path WebAPIのサブディレクトリ
     * @param {Object} body リクエスト時に送信するメッセージボディ
     * @param {Function} preCb 関数実行前に呼び出されるコールバック関数
     * @param {Function} successCb 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} exceptionCb 例外受信時に呼び出されるコールバック関数
     * @param {Function} unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
     * @param {Function} allwaysCb 処理終了後に必ず呼び出されるコールバック関数
     * @return {} 返り値なし
     */
    miiafwCognitolibs_invokeApiGwFunc (url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb) {
      if (this.$authorization.mustAuth) {
        this.miiafwCognitolibs_procUnderAuthed((tokens) => {
          this.miiafwCognitolibs_invokeApiGwFuncWithToken(tokens, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb)
        })
      } else {
        this.miiafwCognitolibs_invokeApiGwFuncWithoutToken(url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb)
      }
    },
    /**
     * miiafwCognitolibs_invokeApiGwFuncが呼び出す、認証されているときに実行を許可する処理の雛形。
     * トークンをセッションスレージから取得出来た場合は認証の必要なAPIを実行し、取得できなかった場合にはアラートメッセージを出力してトップページに遷移させる。
     * @param {authRequiredCb} callback 認証が必要な動作を行うコールバック関数
     * @return {} 返り値なし
     */
    miiafwCognitolibs_procUnderAuthed (callback) {
      const invokeCallback = () => {
        const tokens = this.miiafwCognitolibs_getTokens()
        if (tokens !== null) {
          callback(tokens)
        } else {
          this.miiafwlibs_setLoadingView(false)
          this.miiafwCognitolibs_procAtSignout().then((result) => {
            this.miiafwlibs_alert('許可されていない操作です。(tokens)', () => {
              if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
            })
          })
        }
      }
      // 処理中表示
      this.miiafwlibs_setLoadingView(true)
      if (this.$store.getters['miiaFwStore/isAuthorized']) {
        invokeCallback()
      } else {
        const unwatch = this.$store.watch(
          (state, getters) => {
            return state.miiaFwStore.cognitoAuth.isAuthorized
          },
          (newVal, oldVal) => {
            if (newVal === true) {
              invokeCallback()
            } else {
              this.miiafwlibs_setLoadingView(false)
              this.miiafwCognitolibs_procAtSignout().then((result) => {
                this.miiafwlibs_alert('許可されていない操作です。(Signout)', () => {
                  if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
                })
              })
            }
            unwatch() // 監視を解除
            clearTimeout(timerObj)
          }
        )
        // 5秒以内にトークンを取得できなかった場合はタイムアウト
        const timerObj = setTimeout(() => {
          unwatch()
          const refreshState = this.$store.getters['miiaFwStore/refreshState']
          if (refreshState !== undefined && refreshState.onRefresh) {
            // トークンリフレッシュ中は再帰的に自分を実行する。
            this.miiafwCognitolibs_procUnderAuthed(callback)
          } else {
            this.miiafwCognitolibs_procAtSignout().then((result) => {
              this.miiafwlibs_alert('許可されていない操作です。(unwatch)', () => {
                this.miiafwlibs_setLoadingView(false)
                if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
              })
            })
          }
        }, 5000)
      }
    },
    // API Gatewayの認可対応のAPI実行処理本体
    /**
     * invokeApiGwFuncWithTokenが呼び出す、API-GWが提供する認可処理を行った後API実行を行う処理本体。
     * @param {Object} apigClient aws-api-gateway-clientパッケージによって生成されたクライアント
     * @param {Object} pathParams GETメソッド使用時のパスリクエストパラメータ
     * @param {String} method HTTPメソッド
     * @param {Object} additionalParams クエリが必要なときに入力するパラメータ
     * @param {String} url WebAPIのURL
     * @param {String} path WebAPIのサブディレクトリ
     * @param {Object} body リクエスト時に送信するメッセージボディ
     * @param {Function} preCb 関数実行前に呼び出されるコールバック関数
     * @param {Function} successCb 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} exceptionCb 例外受信時に呼び出されるコールバック関数
     * @param {Function} unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
     * @param {Function} allwaysCb 処理終了後に必ず呼び出されるコールバック関数
     * @param {Boolean} isRetryable 一度だけリトライするかどうか
     * @return {} 返り値なし
     */
    miiafwCognitolibs_invokeApiGwFuncWithAuthProc (apigClient, pathParams, method, additionalParams, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable) {
      apigClient.invokeApi(pathParams, path, method, additionalParams, body).then((result) => {
        if (Object.prototype.hasOwnProperty.call(result.data, 'error')) {
          // Lambdaが例外をスローした場合
          exceptionCb(result.data.error)
        } else {
          successCb(result.data)
        }
      }).catch((error) => {
        // API GWが自動生成するAPIはCredential Errorを検出できないので1回だけリトライする。
        if (isRetryable) {
          this.miiafwCognitolibs_refreshToken().then((argTokens) => {
            this.miiafwCognitolibs_invokeApiGwFuncWithToken(argTokens, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, false)
          }).catch((refreshError) => {
            // miiafwCognitolibs_procAtSignout()は失敗しない。
            // 【要検討】Refresh tokenを使用してAccess tokenをリフレッシュできなかった場合は、Top画面に戻るか再認証してリトライか?
            this.miiafwCognitolibs_procAtSignout().then((result) => {
              this.miiafwlibs_alert(refreshError.message, () => {
                if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
              })
            })
          })
        } else {
          console.error(error)
          unknownCb()
        }
      }).then(() => { // then -> catch -> then = done -> fail -> always
        this.miiafwCognitolibs_setLastAccessDate(Date.now())
        allwaysCb()
      })
    },
    /**
     * API-GWが提供する関数を実行する(POST限定、トークンによる認可処理が必要)。
     * miiafwCognitolibs_invokeApiGwFuncが呼び出す、認可が必要な関数の処理を行う関数である。
     * @param {Object} tokens miiafwCognitolibs_getTokensで取得できるセッション情報
     * @param {String} url WebAPIのURL
     * @param {String} path WebAPIのサブディレクトリ
     * @param {Object} body リクエスト時に送信するメッセージボディ
     * @param {Function} preCb 関数実行前に呼び出されるコールバック関数
     * @param {Function} successCb 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} exceptionCb 例外受信時に呼び出されるコールバック関数
     * @param {Function} unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
     * @param {Function} allwaysCb 処理終了後に必ず呼び出されるコールバック関数
     * @param {Boolean} [isRetryable=true] 一度だけリトライするかどうか
     * @return {} 返り値なし
     */
    miiafwCognitolibs_invokeApiGwFuncWithToken (tokens, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable) {
      if (isRetryable === undefined) {
        isRetryable = true
      }
      preCb()
      const apigClientFactory = require('aws-api-gateway-client').default
      const pathParams = {
        // GET利用時の変数(クエリストリング)
      }
      const method = 'POST'
      let additionalParams = {}
      if (tokens.provider.toLowerCase() === 'cognito') {
        if (Object.prototype.hasOwnProperty.call(this.$authorization, 'method') && this.$authorization.method.toLowerCase() === 'iam') {
          // AWS_IAM
          additionalParams = {
            // AWS_IAMで認可する場合は、ヘッダを設定すると署名の検証で失敗する。
            queryParams: {}
          }
          // AWS_IAMで認可する場合は、HTTPヘッダを設定すると署名の検証で失敗するのでBodyでAccess Tokenを渡す。
          if (Object.prototype.hasOwnProperty.call(body, 'params')) {
            body.access_token = tokens.access_token
          }
          config.credentials.get((error) => {
            if (!error) {
              const setting = {
                invokeUrl: url,
                accessKey: config.credentials.accessKeyId,
                secretKey: config.credentials.secretAccessKey,
                sessionToken: config.credentials.sessionToken,
                region: this.$region
              }
              const apigClient = apigClientFactory.newClient(setting)
              this.miiafwCognitolibs_invokeApiGwFuncWithAuthProc(apigClient, pathParams, method, additionalParams, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable)
            } else {
              this.miiafwCognitolibs_procAtSignout().then((result) => {
                this.miiafwlibs_alert(error, () => {
                  if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
                })
              })
            }
          })
        } else if (Object.prototype.hasOwnProperty.call(this.$authorization, 'method') && this.$authorization.method.toLowerCase() === 'custom_authorizer') {
          additionalParams = {
            // Custom Authorizerで認可する場合は、Authorizationヘッダにid tokenを含める必要あり
            headers: { Authorization: tokens.id_token, 'X-miiafw-token': tokens.access_token },
            queryParams: {}
          }
          const setting = { invokeUrl: url }
          const apigClient = apigClientFactory.newClient(setting)
          this.miiafwCognitolibs_invokeApiGwFuncWithAuthProc(apigClient, pathParams, method, additionalParams, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable)
        } else {
          this.miiafwCognitolibs_procAtSignout().then((result) => {
            this.miiafwlibs_alert('対応していない認可方式です。', () => {
              if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
            })
          })
        }
      } else if (tokens.provider.toLowerCase() === 'google') {
        additionalParams = {
          headers: { Authorization: tokens.id_token },
          queryParams: {}
        }
        const setting = { invokeUrl: url }
        const apigClient = apigClientFactory.newClient(setting)
        this.miiafwCognitolibs_invokeApiGwFuncWithAuthProc(apigClient, pathParams, method, additionalParams, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable)
      } else if (tokens.provider.toLowerCase() === 'facebook') {
        additionalParams = {
          // facebook: accessトークン
          headers: { Authorization: tokens.access_token },
          queryParams: {}
        }
        const setting = { invokeUrl: url }
        const apigClient = apigClientFactory.newClient(setting)
        this.miiafwCognitolibs_invokeApiGwFuncWithAuthProc(apigClient, pathParams, method, additionalParams, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable)
      } else if (tokens.provider.toLowerCase() === 'twitter') {
        additionalParams = {
          // twitter: Authトークンとトークンシークレットを「;」で連結したもの
          headers: { Authorization: tokens.auth_token + ';' + tokens.token_secret },
          queryParams: {}
        }
        const setting = { invokeUrl: url }
        const apigClient = apigClientFactory.newClient(setting)
        this.miiafwCognitolibs_invokeApiGwFuncWithAuthProc(apigClient, pathParams, method, additionalParams, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb, isRetryable)
      } else {
        this.miiafwCognitolibs_procAtSignout().then((result) => {
          this.miiafwlibs_alert('対応していないIDプロバイダが設定されました。', () => {
            if (this.$router.currentRoute.path !== '/') this.$router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
          })
        })
      }
    },
    /**
     * API-GWが提供する関数を実行する(POST限定、トークンによる認可処理が不要)。
     * miiafwCognitolibs_invokeApiGwFuncが呼び出す、認可が不要な関数の処理を行う関数である。
     * @param {String} url WebAPIのURL
     * @param {String} path WebAPIのサブディレクトリ
     * @param {Object} body リクエスト時に送信するメッセージボディ
     * @param {Function} preCb 関数実行前に呼び出されるコールバック関数
     * @param {Function} successCb 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} exceptionCb 例外受信時に呼び出されるコールバック関数
     * @param {Function} unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
     * @param {Function} allwaysCb 処理終了後に必ず呼び出されるコールバック関数
     * @return {} 返り値なし
     */
    miiafwCognitolibs_invokeApiGwFuncWithoutToken (url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb) {
      const invokeApi = (uri, params) => {
        return new Promise((resolve, reject) => {
          this.miiafwlibs_webApiCall(uri, 'POST', {}, params, 'json').then((data) => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            resolve(data)
          }).catch((error) => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            reject({ message: error.message })
          })
        })
      }
      preCb()
      invokeApi(url + path, body).then((data) => {
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // Lambdaが例外をスローした場合
          exceptionCb(data.error)
        } else {
          successCb(data)
        }
      }).catch((error) => {
        console.error(error)
        unknownCb()
      }).then(() => { // then -> catch -> then = done -> fail -> always
        this.miiafwCognitolibs_setLastAccessDate(Date.now())
        allwaysCb()
      })
    },
    // トークンからユーザ属性を取得する。
    miiafwCognitolibs_getUserAttributesFromStore () {
      return new Promise((resolve, reject) => {
        if (this.$store.getters['miiaFwStore/attributes'] !== undefined) {
          resolve(this.$store.getters['miiaFwStore/attributes'])
        } else {
          const unwatch = this.$store.watch(
            (state, getters) => {
              return state.miiaFwStore.cognitoAuth.attributes
            },
            (newVal, oldVal) => {
              if (oldVal === undefined) {
                resolve(this.$store.getters['miiaFwStore/attributes'])
              } else {
                // 処理なし
              }
              unwatch() // 監視を解除
              clearTimeout(timerObj)
            }
          )
          // 5秒以内にトークンからユーザ属性を取得できなかった場合はタイムアウト
          const timerObj = setTimeout(() => {
            unwatch()
            reject({ message: 'ユーザ属性の取得に失敗しました。' })
          }, 5000)
        }
      })
    },
    /**
     * セッションストレージへセッション情報を格納する。
     * @param {Object} tokens セッション情報
     * @param {String} tokens.access_token アクセストークン
     * @param {String} tokens.refresh_token リフレッシュトークン
     * @param {String} tokens.id_token IDトークン
     * @param {String} tokens.auth_token Authトークン
     * @param {String} tokens.token_secret トークンシークレット
     * @param {String} tokens.provider IDプロバイダ
     */
    // Cookieから取得 → そのまま、Bodyから取得 → セッションストレージに格納する。
    miiafwCognitolibs_setTokens (tokens) {
      if (this.miiafwlibs_getSessionInfo('access_token').length < 100 || this.miiafwlibs_getSessionInfo('id_token').length < 100) {
        this.miiafwlibs_tempDataSetItem('access_token', tokens.access_token ? tokens.access_token : '')
        this.miiafwlibs_tempDataSetItem('refresh_token', tokens.refresh_token ? tokens.refresh_token : '')
        this.miiafwlibs_tempDataSetItem('id_token', tokens.id_token ? tokens.id_token : '')
        this.miiafwlibs_tempDataSetItem('auth_token', tokens.auth_token ? tokens.auth_token : '')
        this.miiafwlibs_tempDataSetItem('token_secret', tokens.token_secret ? tokens.token_secret : '')
        this.miiafwlibs_tempDataSetItem('provider', tokens.provider)
      } else {
        // Cookieのサイズ制限があるのでリフレッシュトークンはBodyで取得してセッション
        this.miiafwlibs_tempDataSetItem('refresh_token', tokens.refresh_token ? tokens.refresh_token : '')
        this.miiafwlibs_tempDataSetItem('provider', tokens.provider)
      }
    },
    /**
     * セッションストレージからセッション情報を取得する。
     * @param {} - 引数なし
     * @return {Object} {'access_token': (String)アクセストークン, 'refresh_token': (String)リフレッシュトークン, 'id_token': (String)IDトークン, 'auth_token': (String)Authトークン, 'token_secret': (String)トークンシークレット, 'provider': (String)IDプロバイダ}
     */
    miiafwCognitolibs_getTokens () {
      const ret = {}
      let accessToken = this.miiafwlibs_getSessionInfo('access_token')
      if (accessToken.length === 0) {
        accessToken = this.miiafwlibs_tempDataGetItem('access_token')
      }
      if (accessToken) ret.access_token = accessToken
      const refreshToken = this.miiafwlibs_tempDataGetItem('refresh_token')
      if (refreshToken) ret.refresh_token = refreshToken
      let idToken = this.miiafwlibs_getSessionInfo('id_token')
      if (idToken.length === 0) {
        idToken = this.miiafwlibs_tempDataGetItem('id_token')
      }
      if (idToken) ret.id_token = idToken
      const authToken = this.miiafwlibs_tempDataGetItem('auth_token')
      if (authToken) ret.auth_token = authToken
      const tokenSecret = this.miiafwlibs_tempDataGetItem('token_secret')
      if (tokenSecret) ret.token_secret = tokenSecret
      const provider = this.miiafwlibs_tempDataGetItem('provider')
      if (provider) ret.provider = provider
      if (Object.keys(ret).length === 0) {
        return null
      } else {
        return ret
      }
    },
    /**
     * セッションストレージのセッション情報を消去する。
     * @param {} - 引数なし
     * @return {} 返り値なし
     */
    miiafwCognitolibs_clearTokens () {
      this.miiafwlibs_tempDataRemoveItem('access_token')
      this.miiafwlibs_tempDataRemoveItem('refresh_token')
      this.miiafwlibs_tempDataRemoveItem('id_token')
      this.miiafwlibs_tempDataRemoveItem('auth_token')
      this.miiafwlibs_tempDataRemoveItem('token_secret')
      this.miiafwlibs_tempDataRemoveItem('provider')
      this.miiafwlibs_tempDataRemoveItem('user_id') // ユーザIDも同じタイミングで消去
      this.miiafwlibs_tempDataRemoveItem('last_access') // 最後にWeb APIへアクセスした時間も同じタイミングで消去
      this.miiafwlibs_RemoveSessionInfo(['access_token', 'id_token'])
    },
    /**
     * セッションストレージへユーザIDを登録する（キー: user_id）。
     * @param {String} userId ユーザーID
     * @return {} 返り値なし
     */
    miiafwCognitolibs_setUserIdToStorage (userId) {
      this.miiafwlibs_tempDataSetItem('user_id', userId)
    },
    /**
     * セッションストレージからユーザIDを取得する(。
     * @param {} - 引数なし
     * @return {?String} ユーザーID。
     */
    miiafwCognitolibs_getUserIdToStorage () {
      return this.miiafwlibs_tempDataGetItem('user_id')
    },
    /**
     * セッションストレージへ最後にWeb APIへアクセスした時間を格納する(キー: last_access)。
     * @param {Number} lad Date.now()で取得される時間
     * @return {} 返り値なし
     */
    miiafwCognitolibs_setLastAccessDate (lad) {
      this.miiafwlibs_tempDataSetItem('last_access', String(lad))
    },
    /**
     * セッションストレージから最後にWeb APIへアクセスした時間取得する(キー: last_access)。
     * @param {} - 引数なし
     * @return {Number} Date.now()で記録された時間を取得する。nullの場合は0を返す。
     */
    miiafwCognitolibs_getLastAccessDate () {
      const strLad = this.miiafwlibs_tempDataGetItem('last_access')
      if (strLad === null) {
        return 0
      } else {
        const lad = Number(strLad)
        if (lad.isNaN) {
          return 0
        } else {
          return lad
        }
      }
    },
    // 【後で削除】クレデンシャルを更新する。(S3の更新に必要)
    /**
     * AWSのクレデンシャルを更新する。
     * @param {Object} tokens miiafwCognitolibs_getTokensで取得できるセッション情報
     * @return {} 返り値なし。
     * @throws {Error} エラーメッセージを持つErrorオブジェクトを返す。
     */
    miiafwCognitolibs_updateCredentials (tokens) {
      let loginTo = ''
      const logins = {}
      if (tokens.provider.toLowerCase() === 'cognito') {
        loginTo = 'cognito-idp.' + this.$region + '.amazonaws.com/' + this.$authorization.userPoolId
        logins[loginTo] = tokens.id_token // cognito: idトークン
      } else if (tokens.provider.toLowerCase() === 'google') {
        loginTo = 'accounts.google.com'
        logins[loginTo] = tokens.id_token // google: idトークン
      } else if (tokens.provider.toLowerCase() === 'facebook') {
        loginTo = 'graph.facebook.com'
        logins[loginTo] = tokens.access_token // facebook: accessトークン
      } else if (tokens.provider.toLowerCase() === 'twitter') {
        loginTo = 'api.twitter.com'
        logins[loginTo] = tokens.auth_token + ';' + tokens.token_secret // twitter: Authトークンとトークンシークレットを「;」で連結したもの
      } else {
        throw new Error('対応していないIDプロバイダが設定されました。')
      }
      // config.credentialsを書き換えただけでは反映はされない(この時点では、config.credentials.needsRefresh == true)
      // config.credentials.refreshを実行すると、config.credentials.needsRefresh === falseとなる。
      // config.credentials.getを実行すると、未反映の場合は暗黙的にAWS.config.credentials.refreshが実行される。
      config.update({
        region: this.$region,
        credentials: new CognitoIdentityCredentials({
          region: this.$region,
          IdentityPoolId: this.$authorization.identityPoolId,
          Logins: logins // ES2015(ES5)の記述要領(IE対応)
          // Logins: { [loginTo]: value }  // ES2015(ES6)の記述要領
        })
      })
    },
    /**
     * Facebookが提供するAPIを利用した認証処理を行う。
     * Facebookのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はFacebookの認証ダイアログを開く。
     * @param {Function} [successCb=()=>{}] 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} [exceptionCb=(msg)=>{console.log(msg)}] 例外受信時に呼び出されるコールバック関数
     * @param {Function} [closeCb=()=>{}] 認証ダイアログを閉じた際に呼び出されるコールバック関数
     */
    miiafwCognitolibs_fbCheckStatus (successCb = () => {}, exceptionCb = (msg) => { console.error(msg) }, closeCb = () => {}) {
      // FB.getLoginStatus()は、アクセス先がHTTPSでないとログイン状態を取得できない。
      window.FB.getLoginStatus((response) => {
        if (response && response.authResponse && response.authResponse.accessToken) {
          // 他タブ、ウィンドウ、デバイスで既にログイン済の場合の処理
          const tokens = {
            provider: 'facebook',
            access_token: response.authResponse.accessToken
          }
          this.miiafwCognitolibs_setTokens(tokens)
          this.miiafwCognitolibs_updateIsAuthorized(true)
          this.miiafwCognitolibs_setUserIdToStorage(response.authResponse.userID)
          this.miiafwCognitolibs_setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
          this.miiafwCognitolibs_updateCredentials(tokens) // 【後で削除】S3でAWS SDKを使用するために必要
          successCb()
        } else if (
          (response && !response.authResponse) ||
          (response.error && response.error.code && response.error.code === 190 && response.error.error_subcode && (response.error.error_subcode === 463 || response.error.error_subcode === 467))) {
          // 未認証の場合、アクセストークン有効期限切れの場合(463)、自アカウントが他セッションによりサインアウトされた場合(467)
          window.FB.login(
            (res) => {
              if (res && res.authResponse && res.authResponse.accessToken) {
                const tokens = {
                  provider: 'facebook',
                  access_token: res.authResponse.accessToken
                }
                this.miiafwCognitolibs_setTokens(tokens)
                this.miiafwCognitolibs_updateIsAuthorized(true)
                this.miiafwCognitolibs_setUserIdToStorage(res.authResponse.userID)
                this.miiafwCognitolibs_setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
                this.miiafwCognitolibs_updateCredentials(tokens) // 【後で削除】S3でAWS SDKを使用するために必要
                successCb()
              } else {
                if (!res.authResponse && res.status === 'unknown') {
                  // ユーザに認証ダイアログを閉じられた
                  closeCb()
                } else if (res.error) {
                  // トークン切れとか自アカウントが他セッションによりサインアウトはないはず。
                  exceptionCb(`${res.error.type}(${res.error.message})`)
                } else {
                  exceptionCb('API実行に失敗しました。(原因: アクセス回線障害など)')
                }
              }
            },
            { scope: 'email' }
          )
        } else if (response && response.error) {
          exceptionCb(`${response.error.type}(${response.error.message})`)
        } else {
          // その他のエラー
          exceptionCb('API実行に失敗しました。(原因: アクセス回線障害など)')
        }
      })
    },
    /**
     * Googleが提供するAPIを利用した認証処理を行う（作成途中）。
     * Googleのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はGoogleの認証ダイアログを開く。
     * @param {Function} [successCb=()=>{}] 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} [exceptionCb=(msg)=>{console.log(msg)}] 例外受信時に呼び出されるコールバック関数
     * @param {Function} [closeCb=()=>{}] 認証ダイアログを閉じた際に呼び出されるコールバック関数
     */
    miiafwCognitolibs_googleCheckStatus (successCb = () => {}, exceptionCb = (msg) => { console.error(msg) }, closeCb = () => {}) {
      const siginInFunc = (internalSuccessCb, internalExceptionCb, internalCloseCb) => {
        authInstance.signIn().then((googleUser) => {
          // Useful data for your client-side scripts:
          const profile = googleUser.getBasicProfile()
          // The ID token you need to pass to your backend:
          const authRes = googleUser.getAuthResponse()
          const tokens = {
            provider: 'google',
            access_token: authRes.access_token,
            id_token: authRes.id_token
          }
          this.miiafwCognitolibs_setTokens(tokens)
          this.miiafwCognitolibs_updateIsAuthorized(true)
          this.miiafwCognitolibs_setUserIdToStorage(profile.getName())
          this.miiafwCognitolibs_setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
          this.miiafwCognitolibs_updateCredentials(tokens) // 【後で削除】S3でAWS SDKを使用するために必要
          internalSuccessCb()
        }).catch((error) => {
          if (error.error === 'popup_closed_by_user') {
            internalCloseCb()
          } else {
            internalExceptionCb(error.error)
          }
        })
      }
      const authInstance = gapi.auth2.getAuthInstance() // eslint-disable-line
      authInstance.then(() => { // onInit
        const isSignedIn = authInstance.isSignedIn.get()
        if (isSignedIn) {
          const googleUser = authInstance.currentUser.get()
          const authRes = googleUser.getAuthResponse()
          const tokens = {
            provider: 'google',
            access_token: authRes.access_token,
            id_token: authRes.id_token
          }
          this.miiafwCognitolibs_setTokens(tokens) // トークンはリフレッシュ後でもSession StrageやCookieに残るので不要
          const profile = googleUser.getBasicProfile() // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
          this.miiafwCognitolibs_setUserIdToStorage(profile.getName()) // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
          this.miiafwCognitolibs_updateCredentials(tokens) // リフレッシュ後に揮発するので要設定【後で削除】S3でAWS SDKを使用するために必要
          this.miiafwCognitolibs_updateIsAuthorized(true) // リフレッシュ後に揮発するので要設定
          successCb()
        } else {
          // 未認証の場合、自アカウントが他セッションによりサインアウトされた場合
          siginInFunc(successCb, exceptionCb, closeCb)
        }
      }, (error) => { // onError
        // TODO: トークン有効期限切れの場合の処理
        exceptionCb(error)
      })
    },
    /**
     * Twitterが提供するAPIを利用した認証処理を行う（作成途中）。
     * Twitterのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はTwitterの認証ダイアログを開く。
     * @param {Function} [successCb=()=>{}] 関数実行成功後に呼び出されるコールバック関数
     * @param {Function} [exceptionCb=(msg)=>{console.log(msg)}] 例外受信時に呼び出されるコールバック関数
     * @param {Function} [closeCb=()=>{}] 認証ダイアログを閉じた際に呼び出されるコールバック関数
     */
    miiafwCognitolibs_twitterCheckStatus (successCb = () => {}, exceptionCb = (msg) => { console.error(msg) }, closeCb = () => {}) {
      this.miiafwlibs_alert('作成中です！')
    }
  }
}
/**
 * 認証の必要なAPIを実行するコールバック関数
 * @callback authRequiredCb
 * @param {Object} tokens miiafwCognitolibs_getTokensで取得されるセッション情報
 */
</script>
